import jwtDecode from "jwt-decode";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Button } from "../../components";
import { authActions } from "../../context/auth-slice";
import { adminList, rolesToRestrict } from "../../constants/roles";
import {getCurrentPortal} from "src/utils/utils";
import { logout } from "src/constants/global-const";
import { tokenStore } from "src/utils/tokenStore";
import { refreshAccessToken } from "src/utils/refreshTokenApi";

// Wipe the short-lived auth cookies (`idToken`, `token`) across every
// plausible (path, domain) combination. The `refreshToken` cookie is left
// in place — the backend owns its lifecycle and the refresh API reads it.
const clearAuthCookies = () => {
  const authCookieNames = ["idToken", "token"];
  const hostname = window.location.hostname;
  const hostParts = hostname.split(".");
  const domains: string[] = [""];
  for (let i = 0; i < hostParts.length; i++) {
    const d = hostParts.slice(i).join(".");
    if (d) {
      domains.push(d);
      domains.push("." + d);
    }
  }
  const paths = ["/", window.location.pathname, ""];
  authCookieNames.forEach((name) => {
    paths.forEach((path) => {
      domains.forEach((domain) => {
        const pathAttr = path ? `; path=${path}` : "";
        const domainAttr = domain ? `; domain=${domain}` : "";
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathAttr}${domainAttr}`;
        document.cookie = `${name}=; Max-Age=0${pathAttr}${domainAttr}`;
      });
    });
  });
};

const readCookie = (cookieName: string): string | null => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }
  return null;
};

const Login = (): JSX.Element => {
  const dispatch = useDispatch();

  const validateLogin = useCallback(async (): Promise<void> => {
    // ── 1. Bootstrap memory from any auth cookies present ──
    // First login after OAuth: the redirect set `idToken` and `token`
    // cookies. Read them into memory before we wipe the cookies.
    // On reload / duplicate tab: those cookies are already gone, but the
    // `refreshToken` cookie is still available — that's what step 2 uses.
    const accessFromCookie = readCookie("token");
    const idTokenFromCookie = readCookie("idToken");
    if (accessFromCookie || idTokenFromCookie) {
      tokenStore.setTokens({
        accessToken: accessFromCookie,
        idToken: idTokenFromCookie,
      });
      clearAuthCookies();
    }

    // ── 2. Always try refresh ──
    // On first login this upgrades the OAuth tokens to a backend-issued
    // set. On reload / duplicate tab this rebuilds memory from the
    // `refreshToken` cookie that persists across JS contexts.
    let activeAccessToken = tokenStore.getAccessToken();
    let activeIdToken = tokenStore.getIdToken();
    try {
      const refreshed = await refreshAccessToken();
      activeAccessToken = refreshed.accessToken || activeAccessToken;
      activeIdToken = refreshed.idToken || activeIdToken;
    } catch (err) {
      console.warn("Initial token refresh failed", err);
    }

    // ── 3. If we still have nothing, kick off OAuth login ──
    if (!activeAccessToken && !activeIdToken) {
      window.location.href = `${import.meta.env.VITE_LOGIN_BASE_URL}/?response_type=token&client_id=${import.meta.env.VITE_CLIENT_ID}&redirect_uri=${window.location.href}`;
      return;
    }

    // ── 4. Decode and hydrate Redux ──
    const decodedToken: any = activeIdToken ? jwtDecode(activeIdToken as string) : null;
    const decodedName: any = decodedToken;

    const rawSession = localStorage.getItem("medvol_active_session");
    const existingSession = rawSession ? JSON.parse(rawSession) : null;
    if (existingSession && existingSession.userId !== decodedToken?.sub) {
      window.alert("Another login was detected in a different portal or tab. Hence, you are being logged out from other sessions. Please log in again to continue.");
      logout(false, false);
      return;
    }
    localStorage.setItem(
      "medvol_active_session",
      JSON.stringify({
        userId: decodedToken?.sub,
        portal: getCurrentPortal(),
        timestamp: Date.now(),
      })
    );

    const mvRole: string | undefined = decodedToken?.["custom:mv_role"]; //M10-15057
    const isStockist = mvRole === "Stockist" || decodedToken?.["custom:role"] === "Stockist"; //M10-15057
    dispatch(authActions.setRestricted(rolesToRestrict.includes(mvRole as string) || !!mvRole?.startsWith("L") || isStockist)); //M10-15057

    dispatch(authActions.login(decodedToken));
    dispatch(authActions.setIdTokenDetails(decodedToken));
    dispatch(authActions.setLoggedInUser(decodedName?.name || ""));
    dispatch(authActions.setIsAdmin(adminList.includes(decodedToken?.["custom:mv_role"])));
    dispatch(authActions.setIsClient(decodedToken?.["custom:mv_role"] === "Client"));
  }, [dispatch]);

  useEffect(() => {
    validateLogin();
  }, [validateLogin]);

  return (
    <div className="flex flex-col items-center justify-center w-screen h-screen">
      <div>
        <h1 className="text-4xl font-bold text-center">You are not Logged In!</h1>
        <h2 className="text-2xl text-center mt-1 mb-3 text-[rgba(0,0,0,0.4)] font-semibold">Please click on the below button to login</h2>
      </div>
      <Button className="text-white bg-blue-600" onClick={validateLogin}>
        Click here to login
      </Button>
    </div>
  );
};

export default Login;
